import {RUINS_INCOME_GLOW, checkIfRuinsIncomeGlowSparkles} from '../configs/ruinsIncomeGlow';
import {
  createSparkle,
  resolveBreath,
  resolveBreathAlpha,
  resolveGlowFadeIn,
  resolveIconGlyphAlpha,
  resolveIconPlacement,
  resolveIconTextureLayout,
  resolveRimTextureLayout,
  resolveShimmerFrame,
  resolveShimmerGradientStops,
  resolveShimmerPeriodMs,
  resolveSparkleDelayMs,
  resolveSparklePose,
} from '../utils/effects/buildRuinsIncomeGlow';

// Событие сцены Phaser «кадр»: приходит со временем кадра
const FRAME_EVENT = 'update';

const WHITE = 'rgba(255, 255, 255, 1)';
const TRANSPARENT_WHITE = 'rgba(255, 255, 255, 0)';

const createDocumentCanvas = (width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const checkIfTargetDrawable = (target) => (
  Boolean(target?.hexKey)
  && Number.isFinite(target.x) && Number.isFinite(target.y) && Number.isFinite(target.depth)
  && target.width > 0 && target.height > 0
);

/**
 * СВЕЧЕНИЕ РУИН В РАУНДЫ С УВЕЛИЧЕННЫМ ДОХОДОМ (вид — configs/ruinsIncomeGlow):
 * поверх жетона руин дышит белесый ореол по краю, светится и переливается
 * волной света иконка очков, а на великих руинах еще и вспыхивают звездочки
 * (checkIfRuinsIncomeGlowSparkles). Когда светиться, решает
 * рендерер поля: он передает в sync жетоны руин, пока идет окно увеличенного
 * дохода, и пустой список вне его.
 *
 * СПРАЙТЫ СВЕЧЕНИЯ ПРИНАДЛЕЖАТ ЕМУ, а не ячейке, и живут, пока жетон светится:
 * поле перерисовывается на каждое обновление стора и спрайты жетонов создаются
 * заново — свечение, привязанное к ним, начиналось бы заново каждым ходом.
 * Рендерер после перерисовки лишь сообщает, где сейчас стоят руины (sync).
 *
 * ВРЕМЯ ИДЕТ ПО КАДРАМ, а не по настенным часам: в скрытой вкладке свечение
 * стоит и после возврата продолжает с того же места.
 *
 * Картинки рисуются в память один раз на сцену. Силуэт иконки вырезается из
 * уже загруженной картинки руин, поэтому совпадает с нарисованной иконкой
 * точно; волна по нему — заранее нарисованные кадры. Не вышло нарисовать
 * иконку — светятся только ореол и звездочки.
 *
 * Свечение — украшение: его сбой не должен ронять кадр или перерисовку поля,
 * поэтому после первой же ошибки оно до конца сцены отключается.
 */
export default class RuinsIncomeGlow {
  /**
   * @param {Phaser.Scene} scene
   * @param {Object} [options]
   * @param {function(): number} [options.rng] — источник случайности (в тестах — заглушка)
   * @param {function(number, number): HTMLCanvasElement} [options.createCanvas] —
   *   холст для черновой работы с картинками (в тестах — заглушка)
   */
  constructor(scene, {rng = Math.random, createCanvas = createDocumentCanvas} = {}) {
    this.scene = scene;
    this.rng = rng;
    this.createCanvas = createCanvas;
    // Светящиеся жетоны: ключ ячейки -> место жетона, его время и спрайты
    this.glows = new Map();
    // Размер картинки руин, из которой вырезан силуэт иконки (null — иконки нет)
    this.iconImage = null;
    this.isIconFailed = false;
    this.isListening = false;
    this.isFailed = false;
  }

  /**
   * Поле перерисовано: вот какие жетоны сейчас светятся и где они стоят.
   * Новые разгораются, у прежних свечение продолжается, остальные гаснут
   * @param {Array<{hexKey: string, tokenType: string, x: number, y: number, width: number, height: number, depth: number}>} targets —
   *   тип жетона, центр, размер на поле и слой его спрайта
   */
  sync(targets = []) {
    this.guard(() => {
      const nextKeys = new Set();

      targets.filter(checkIfTargetDrawable).forEach(({hexKey, tokenType, x, y, width, height, depth}) => {
        nextKeys.add(hexKey);
        let glow = this.glows.get(hexKey);
        // На ячейке теперь другой жетон (редактор поля): звездочки у него
        // могут быть, а могут и не быть — свечение зажигается заново
        if (glow && glow.token.tokenType !== tokenType) {
          this.stop(hexKey);
          glow = null;
        }
        if (!glow) {
          this.start(hexKey, {tokenType, x, y, width, height, depth});
          return;
        }
        const {token} = glow;
        if (token.depth !== depth) {
          token.depth = depth;
          this.setDepths(glow);
        }
        if (token.x !== x || token.y !== y || token.width !== width || token.height !== height) {
          Object.assign(token, {x, y, width, height});
          this.place(glow);
        }
      });

      [...this.glows.keys()].filter((hexKey) => !nextKeys.has(hexKey)).forEach((hexKey) => this.stop(hexKey));

      this.updateListening();
    });
  }

  /**
   * Шаг времени: ореол и иконка дышат, волна идет по иконке, звездочки вспыхивают
   * @param {number} delta — время кадра, мс
   */
  update(delta) {
    this.guard(() => {
      const stepMs = Math.min(delta, RUINS_INCOME_GLOW.maxStepMs);
      if (!(stepMs > 0)) {
        return;
      }

      this.glows.forEach((glow) => {
        glow.elapsedMs += stepMs;

        if (glow.shimmer) {
          glow.shimmer.elapsedMs += stepMs;
          if (glow.shimmer.elapsedMs >= glow.shimmer.periodMs) {
            glow.shimmer.elapsedMs -= glow.shimmer.periodMs;
            glow.shimmer.periodMs = resolveShimmerPeriodMs(this.rng);
          }
        }

        glow.sparkles.forEach((sparkle) => this.stepSparkle(sparkle, stepMs));

        this.render(glow);
      });
    });
  }

  /** Звездочка: ждет своей вспышки, вспыхивает, гаснет и снова ждет */
  stepSparkle(sparkle, stepMs) {
    const {durationMs} = RUINS_INCOME_GLOW.sparkles;
    if (sparkle.flash) {
      sparkle.elapsedMs += stepMs;
      if (sparkle.elapsedMs >= durationMs) {
        sparkle.flash = null;
        sparkle.delayMs = resolveSparkleDelayMs(this.rng);
      }
      return;
    }
    sparkle.delayMs -= stepMs;
    if (sparkle.delayMs <= 0) {
      // Вспыхивает в новом месте
      sparkle.flash = createSparkle(this.rng);
      sparkle.elapsedMs = Math.min(-sparkle.delayMs, durationMs);
    }
  }

  /** Зажечь свечение у жетона ячейки */
  start(hexKey, token) {
    const {depthOffsets, rim, sparkles, shimmer, breathPeriodMs} = RUINS_INCOME_GLOW;
    const sparkleCount = checkIfRuinsIncomeGlowSparkles(token.tokenType) ? sparkles.count : 0;
    if (!this.ensureRimTexture() || (sparkleCount > 0 && !this.ensureSparkleTexture())) {
      return;
    }
    const hasIcon = this.ensureIconTextures();

    const glow = {
      token,
      // Время горения: от него зависит, насколько свечение разгорелось
      elapsedMs: 0,
      // Дыхание у каждого жетона в своей фазе — ореолы на поле дышат вразнобой
      breathOffsetMs: this.rng() * breathPeriodMs,
      rim: null,
      icon: null,
      shimmer: null,
      sparkles: [],
    };
    // Запись заводится до сборки спрайтов: сорвись сборка на середине, stop
    // уберет все, что успело появиться
    this.glows.set(hexKey, glow);
    try {
      glow.rim = this.createSprite(rim.texture.key, token.depth + depthOffsets.rim);
      if (hasIcon) {
        glow.icon = this.createSprite(RUINS_INCOME_GLOW.icon.textureKey, token.depth + depthOffsets.icon);
        const periodMs = resolveShimmerPeriodMs(this.rng);
        glow.shimmer = {
          sprite: this.createSprite(shimmer.textureKey, token.depth + depthOffsets.shimmer, '0'),
          periodMs,
          // Волна у каждого жетона в своем месте цикла
          elapsedMs: this.rng() * periodMs,
          frame: null,
        };
      }
      for (let index = 0; index < sparkleCount; index += 1) {
        glow.sparkles.push({
          sprite: this.createSprite(sparkles.texture.key, token.depth + depthOffsets.sparkle),
          flash: null,
          elapsedMs: 0,
          // Звездочки с самого начала вспыхивают вразнобой, а не залпом
          delayMs: this.rng() * sparkles.delayMaxMs,
        });
      }
    } catch (error) {
      this.stop(hexKey);
      throw error;
    }

    this.place(glow);
    this.render(glow);
  }

  /** Погасить свечение у жетона ячейки */
  stop(hexKey) {
    const glow = this.glows.get(hexKey);
    if (!glow) {
      return;
    }
    this.glows.delete(hexKey);
    glow.rim?.destroy?.();
    glow.icon?.destroy?.();
    glow.shimmer?.sprite?.destroy?.();
    glow.sparkles.forEach((sparkle) => sparkle.sprite?.destroy?.());
  }

  /** Погасить все: поле разбирается */
  clear() {
    [...this.glows.keys()].forEach((hexKey) => {
      try {
        this.stop(hexKey);
      } catch (error) {
        // Сцена уже разбирается: спрайт мог быть уничтожен раньше нас
      }
    });
    this.glows.clear();
    this.updateListening();
  }

  destroy() {
    this.clear();
  }

  /** Свет складывается с жетоном, а не закрашивает его */
  createSprite(textureKey, depth, frame) {
    const sprite = this.scene.add.image(0, 0, textureKey, frame);
    sprite.setOrigin(0.5, 0.5);
    sprite.setDepth(depth);
    sprite.setBlendMode('ADD');
    sprite.setAlpha(0);
    return sprite;
  }

  /**
   * Слой меняется только вместе со слоем жетона: setDepth на каждом кадре
   * заставлял бы сцену пересортировывать все, что на ней нарисовано
   */
  setDepths(glow) {
    const {depthOffsets} = RUINS_INCOME_GLOW;
    const {depth} = glow.token;
    glow.rim?.setDepth(depth + depthOffsets.rim);
    glow.icon?.setDepth(depth + depthOffsets.icon);
    glow.shimmer?.sprite?.setDepth(depth + depthOffsets.shimmer);
    glow.sparkles.forEach((sparkle) => sparkle.sprite?.setDepth(depth + depthOffsets.sparkle));
  }

  /** Поставить ореол и иконку на жетон — при появлении и когда жетон сдвинулся */
  place(glow) {
    const {token} = glow;
    const {hexWidth, hexHeight} = resolveRimTextureLayout();
    glow.rim.setPosition(token.x, token.y);
    glow.rim.setScale(token.width / hexWidth, token.height / hexHeight);

    if (glow.icon && this.iconImage) {
      const {x, y, scaleX, scaleY} = resolveIconPlacement(token, this.iconImage);
      [glow.icon, glow.shimmer.sprite].forEach((sprite) => {
        sprite.setPosition(x, y);
        sprite.setScale(scaleX, scaleY);
      });
    }
  }

  /** Яркость ореола и иконки, кадр волны, звездочки — по текущему времени */
  render(glow) {
    const {token} = glow;
    const fadeIn = resolveGlowFadeIn(glow.elapsedMs);
    const breath = resolveBreath(glow.elapsedMs + glow.breathOffsetMs);

    glow.rim.setAlpha(resolveBreathAlpha(RUINS_INCOME_GLOW.rim, breath) * fadeIn);
    glow.icon?.setAlpha(resolveBreathAlpha(RUINS_INCOME_GLOW.icon, breath) * fadeIn);

    if (glow.shimmer) {
      const frame = resolveShimmerFrame(glow.shimmer.elapsedMs);
      if (frame === null) {
        glow.shimmer.sprite.setAlpha(0);
      } else {
        // Кадр переключается, только когда сменился
        if (frame !== glow.shimmer.frame) {
          glow.shimmer.sprite.setFrame(String(frame));
          glow.shimmer.frame = frame;
        }
        glow.shimmer.sprite.setAlpha(RUINS_INCOME_GLOW.shimmer.alpha * fadeIn);
      }
    }

    glow.sparkles.forEach(({sprite, flash, elapsedMs}) => {
      if (!flash) {
        sprite.setAlpha(0);
        return;
      }
      const pose = resolveSparklePose(flash, elapsedMs / RUINS_INCOME_GLOW.sparkles.durationMs, token);
      sprite.setPosition(pose.x, pose.y);
      sprite.setDisplaySize(pose.size, pose.size);
      sprite.setAngle(pose.angleDeg);
      sprite.setAlpha(pose.alpha * fadeIn);
    });
  }

  /**
   * Холст текстуры сцены, на котором есть чем рисовать; иначе текстура не
   * остается: невидимое свечение считалось бы показанным
   * @returns {{texture: Object, context: CanvasRenderingContext2D}|null}
   */
  createTextureCanvas(key, width, height, requiredMethod) {
    const textures = this.scene.textures;
    const texture = textures?.createCanvas?.(key, width, height);
    const context = texture?.context;
    if (!context?.[requiredMethod]) {
      if (texture) {
        textures.remove?.(key);
      }
      return null;
    }
    return {texture, context};
  }

  /** Ореол — светящийся контур шестиугольника жетона */
  ensureRimTexture() {
    const {key} = RUINS_INCOME_GLOW.rim.texture;
    if (this.scene.textures?.exists?.(key)) {
      return true;
    }
    const layout = resolveRimTextureLayout();
    const canvas = this.createTextureCanvas(key, layout.width, layout.height, 'stroke');
    if (!canvas) {
      return false;
    }

    const {context, texture} = canvas;
    context.strokeStyle = WHITE;
    context.lineJoin = 'round';
    context.lineWidth = layout.lineWidth;
    context.shadowColor = WHITE;
    context.shadowBlur = layout.blur;
    // Дважды: одного прохода размытому свету мало
    [0, 1].forEach(() => {
      context.beginPath();
      layout.points.forEach(({x, y}, index) => (index ? context.lineTo(x, y) : context.moveTo(x, y)));
      context.closePath();
      context.stroke();
    });
    texture.refresh();
    return true;
  }

  /** Звездочка — четыре луча крестом и яркая сердцевина */
  ensureSparkleTexture() {
    const {key, size, rayWidthShare, coreShare} = RUINS_INCOME_GLOW.sparkles.texture;
    if (this.scene.textures?.exists?.(key)) {
      return true;
    }
    const canvas = this.createTextureCanvas(key, size, size, 'createRadialGradient');
    if (!canvas) {
      return false;
    }

    const {context, texture} = canvas;
    const center = size / 2;
    const rayWidth = size * rayWidthShare;
    const rayGradient = context.createRadialGradient(center, center, 0, center, center, center);
    rayGradient.addColorStop(0, WHITE);
    rayGradient.addColorStop(1, TRANSPARENT_WHITE);
    context.fillStyle = rayGradient;
    // Луч — узкий ромб от края до края: вертикальный и горизонтальный
    [[rayWidth, center], [center, rayWidth]].forEach(([halfWidth, halfHeight]) => {
      context.beginPath();
      context.moveTo(center - halfWidth, center);
      context.lineTo(center, center - halfHeight);
      context.lineTo(center + halfWidth, center);
      context.lineTo(center, center + halfHeight);
      context.closePath();
      context.fill();
    });
    const coreGradient = context.createRadialGradient(center, center, 0, center, center, size * coreShare);
    coreGradient.addColorStop(0, WHITE);
    coreGradient.addColorStop(1, TRANSPARENT_WHITE);
    context.fillStyle = coreGradient;
    context.fillRect(0, 0, size, size);
    texture.refresh();
    return true;
  }

  /**
   * Силуэт иконки очков и кадры волны по нему. Силуэт вырезается из картинки
   * руин: темные пиксели иконки становятся белыми, светлый камень — прозрачным
   * @returns {boolean} есть ли иконка
   */
  ensureIconTextures() {
    if (this.iconImage) {
      return true;
    }
    if (this.isIconFailed) {
      return false;
    }

    const {icon, shimmer} = RUINS_INCOME_GLOW;
    const textures = this.scene.textures;
    const source = textures?.exists?.(icon.sourceTextureKey)
      ? textures.get?.(icon.sourceTextureKey)?.getSourceImage?.()
      : null;
    // Картинки иконки уже нарисованы: текстуры общие на всю игру, а рендерер
    // поля мог смениться
    const isDrawn = textures?.exists?.(icon.textureKey) && textures.exists(shimmer.textureKey);
    if (!(source?.width > 0) || !(source?.height > 0) || (!isDrawn && !this.drawIconTextures(source))) {
      // Больше не пробуем: вырезать силуэт на каждой перерисовке поля было бы
      // дорого, а картинка руин загружена до начала сцены
      this.isIconFailed = true;
      return false;
    }

    this.iconImage = {width: source.width, height: source.height};
    return true;
  }

  /**
   * Нарисовать силуэт иконки и кадры волны по нему
   * @param {HTMLImageElement|HTMLCanvasElement} source — картинка руин
   * @returns {boolean} получилось ли
   */
  drawIconTextures(source) {
    const {icon, shimmer} = RUINS_INCOME_GLOW;
    const textures = this.scene.textures;
    // Остатки прошлой неудачной попытки
    [icon.textureKey, shimmer.textureKey].forEach((key) => textures.exists(key) && textures.remove?.(key));

    const glyph = this.cutIconGlyph(source);
    const layout = resolveIconTextureLayout();
    const iconCanvas = glyph && this.createTextureCanvas(icon.textureKey, layout.cellWidth, layout.cellHeight, 'drawImage');
    if (!iconCanvas) {
      return false;
    }
    iconCanvas.context.drawImage(glyph, layout.padding, layout.padding);
    iconCanvas.texture.refresh();

    if (!this.drawShimmerFrames(glyph, layout)) {
      textures.remove?.(icon.textureKey);
      return false;
    }
    return true;
  }

  /**
   * Белый силуэт иконки в масштабе textureScale
   * @param {HTMLImageElement|HTMLCanvasElement} source — картинка руин
   * @returns {HTMLCanvasElement|null}
   */
  cutIconGlyph(source) {
    const {icon} = RUINS_INCOME_GLOW;
    const {x, y, width, height} = icon.sourceRect;
    const {glyphWidth, glyphHeight} = resolveIconTextureLayout();

    const cut = this.createCanvas(width, height);
    const cutContext = cut?.getContext?.('2d');
    const glyph = this.createCanvas(glyphWidth, glyphHeight);
    const glyphContext = glyph?.getContext?.('2d');
    if (!cutContext?.getImageData || !glyphContext?.drawImage) {
      return null;
    }

    cutContext.drawImage(source, x, y, width, height, 0, 0, width, height);
    let pixels;
    try {
      pixels = cutContext.getImageData(0, 0, width, height);
    } catch (error) {
      // Пиксели картинки прочитать нельзя (чужой источник) — иконки не будет,
      // а ореол и звездочки останутся
      return null;
    }
    const {data} = pixels;
    for (let index = 0; index < data.length; index += 4) {
      const luma = (data[index] + data[index + 1] + data[index + 2]) / 3;
      const alpha = resolveIconGlyphAlpha(luma, data[index + 3]);
      data[index] = 255;
      data[index + 1] = 255;
      data[index + 2] = 255;
      data[index + 3] = Math.round(alpha * 255);
    }
    cutContext.putImageData(pixels, 0, 0);
    // Уменьшается уже готовый силуэт: края остаются мягкими
    glyphContext.drawImage(cut, 0, 0, glyphWidth, glyphHeight);
    return glyph;
  }

  /**
   * Кадры волны — сеткой на одной картинке: в кадре полоса света по
   * диагонали, оставленная только там, где силуэт иконки (и чуть вокруг —
   * ореол света)
   */
  drawShimmerFrames(glyph, layout) {
    const {shimmer} = RUINS_INCOME_GLOW;
    const rows = Math.ceil(shimmer.frameCount / shimmer.frameColumns);
    const atlas = this.createTextureCanvas(
      shimmer.textureKey, shimmer.frameColumns * layout.cellWidth, rows * layout.cellHeight, 'drawImage'
    );
    const frameCanvas = this.createCanvas(layout.cellWidth, layout.cellHeight);
    const frameContext = frameCanvas?.getContext?.('2d');
    if (!atlas || !frameContext?.createLinearGradient) {
      if (atlas) {
        this.scene.textures.remove?.(shimmer.textureKey);
      }
      return false;
    }

    const {cellWidth, cellHeight, padding} = layout;
    for (let frame = 0; frame < shimmer.frameCount; frame += 1) {
      frameContext.globalCompositeOperation = 'source-over';
      frameContext.shadowBlur = 0;
      frameContext.clearRect(0, 0, cellWidth, cellHeight);
      // Диагональ клетки: из нижнего левого угла в верхний правый
      const gradient = frameContext.createLinearGradient(0, cellHeight, cellWidth, 0);
      resolveShimmerGradientStops(frame)
        .forEach(([offset, alpha]) => gradient.addColorStop(offset, `rgba(255, 255, 255, ${alpha})`));
      frameContext.fillStyle = gradient;
      frameContext.fillRect(0, 0, cellWidth, cellHeight);

      frameContext.globalCompositeOperation = 'destination-in';
      frameContext.shadowColor = WHITE;
      frameContext.shadowBlur = shimmer.glowBlur;
      frameContext.drawImage(glyph, padding, padding);

      const x = (frame % shimmer.frameColumns) * cellWidth;
      const y = Math.floor(frame / shimmer.frameColumns) * cellHeight;
      atlas.context.drawImage(frameCanvas, x, y);
      atlas.texture.add(String(frame), 0, x, y, cellWidth, cellHeight);
    }
    frameContext.globalCompositeOperation = 'source-over';
    frameContext.shadowBlur = 0;
    atlas.texture.refresh();
    return true;
  }

  /** Кадры слушаем, только пока есть чему светиться */
  updateListening() {
    const events = this.scene.events;
    const shouldListen = !this.isFailed && this.glows.size > 0;

    if (shouldListen && !this.isListening && events?.on) {
      events.on(FRAME_EVENT, this.handleFrame, this);
      this.isListening = true;
    } else if (!shouldListen && this.isListening) {
      events?.off?.(FRAME_EVENT, this.handleFrame, this);
      this.isListening = false;
    }
  }

  handleFrame(time, delta) {
    this.update(delta);
  }

  /** Сбой свечения не должен уронить кадр или перерисовку поля — свечение отключается */
  guard(action) {
    if (this.isFailed) {
      return;
    }
    try {
      action();
    } catch (error) {
      console.warn('[RuinsIncomeGlow] Свечение руин отключено: сбой', error);
      this.isFailed = true;
      this.clear();
    }
  }
}
